import React, { useContext, useEffect } from 'react';
import Link from 'next/link';
import mergeAllOf from '@stoplight/json-schema-merge-allof';
import snarkdown from 'snarkdown';
import {
  OpenAPIObject,
  PathItemObject,
  SchemaObject,
  isSchemaObject,
  ParameterObject,
  isReferenceObject,
  ReferenceObject,
} from 'openapi3-ts';

import { getFormattedExample, getRouteFromDefinition } from './schemaUtils';
import ModelSchema from './ModelSchema';

import { ClientPlatform } from '../types';
import { replaceApiRouteLinks } from '../utilities';
import OpenAPIContext from '../../../contexts/docs/openapi';
import OpenAPILinkContext from '../../../contexts/docs/openapi/link';
import Context from '../../../contexts/docs';
import SchemaLayout from './SchemaLayout/SchemaLayout';

import styles from './index.module.scss';
import OpenAPIExchangeContext from 'src/contexts/docs/openapi/exchange';
import OpenAPICoreExchangeContext from 'src/contexts/docs/openapi/core-exchange';
import LinkSchema from './LinkSchema';
import RequestSchema from './RequestSchema';
import ResponseSchema from './ResponseSchema';

export interface BaseProps {
  children?: React.ReactNode;
  collapse?: boolean;
  expandText?: string;
  hideText?: string;
  unhideFlag?: string;
  exampleIndex?: number;
  tableOnly?: boolean;
  exampleTitle?: string;
  exchangeSpec?: 'aggregator';
  coreExchangeSpec?: '6.1';
}
interface ModelProps extends BaseProps {
  type: 'model';
  model: string;
}
interface RouteProps extends BaseProps {
  type: 'request' | 'response';
  route: string;
  method?: 'GET' | 'POST' | 'PUT';
}

interface LinkProps extends BaseProps {
  type: 'link';
  sdk: ClientPlatform;
  method?: string;
}

// FUTURE Refactoring steps:
// 1. Definiton is pretty massive. Do we need to be passing it around? Maybe there's
// a simpler way to do the automagic linking thing.
// 2. Do we need to explicitly pass in tableOnly? Could I figure it out programmatically?
// 3. We can probably drop simplifyForBill
// 4. Maybe simplify the "remove slash / generate kebab case" logic

export type Props = RouteProps | ModelProps | LinkProps;

export type SchemaObjectEntry = [string, SchemaObject];

const Schema: React.FC<Props> = (props: Props) => {
  const { selectedCode, dispatch } = useContext(Context);
  const [simplifyForBill, setSimplifyForBill] = React.useState(false);
  let definition: OpenAPIObject;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSimplifyForBill(params.get('forBill') === 'true');
  }, []);

  const pxSchemas = useContext(OpenAPIExchangeContext);
  const cxSchemas = useContext(OpenAPICoreExchangeContext);
  const defaultSchema = useContext(OpenAPIContext);
  if (props.exchangeSpec != null) {
    definition = pxSchemas[props.exchangeSpec];
  } else if (props.coreExchangeSpec != null) {
    definition = cxSchemas[props.coreExchangeSpec];
  } else {
    definition = defaultSchema;
  }

  const linkDefinition: OpenAPIObject = useContext(OpenAPILinkContext);

  // OpenAPI schema is not loaded into context yet
  if (definition?.paths == null) {
    return null;
  }

  switch (props.type) {
    case 'model':
      // The "Model" type -- used mostly for webhooks and "Schema-only" objects
      return (
        <ModelSchema
          modelName={props.model}
          definition={definition}
          simplifyForBill={simplifyForBill}
          {...props}
        />
      );

    case 'link':
      // Schema for Link methods -- used for client side SDKs
      return (
        <LinkSchema
          definition={linkDefinition}
          sdk={props.sdk}
          methodName={props.method}
          simplifyForBill={simplifyForBill}
          {...props}
        />
      );

    case 'request':
      return (
        <RequestSchema
          definition={definition}
          route={props.route}
          method={props.method}
          simplifyForBill={simplifyForBill}
          {...props}
        />
      );

    case 'response':
      return (
        <ResponseSchema
          definition={definition}
          route={props.route}
          method={props.method}
          simplifyForBill={simplifyForBill}
          {...props}
        />
      );

    default:
      // We threw an error earlier. Do we still want to?
      return null;
  }
};

Schema.displayName = 'Schema';

export default Schema;
